import { createFileRoute, Link } from "@tanstack/react-router";
import { getErrorMessage } from "@/lib/errorMessage";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InlineConsentCard } from "@/components/consent/InlineConsentCard";

export const Route = createFileRoute("/_authenticated/account_/notifications")({
  head: () => ({
    meta: [
      { title: "Notification preferences · TruWe" },
      {
        name: "description",
        content: "Choose how TruWe keeps you updated — WhatsApp, email and browser notifications.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: ({ error, reset }: { error: unknown; reset: () => void }) => (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="font-display text-2xl font-semibold">Couldn't load your preferences</h1>
      <p className="mt-2 text-sm text-muted-foreground">{getErrorMessage(error)}</p>
      <Button className="mt-4 rounded-full" onClick={reset}>
        Try again
      </Button>
    </div>
  ),
  component: NotificationPreferencesPage,
});

function NotificationPreferencesPage() {
  return (
    <main className="mx-auto max-w-2xl px-4 py-10">
      <Link
        to="/account"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to your account
      </Link>

      <h1 className="mt-4 font-display text-2xl font-semibold text-foreground">
        How we keep in touch
      </h1>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        Pick the channels that suit you. Changes save straight away, and order-critical messages
        (payment, delivery, refunds) are always sent no matter what you choose here.
      </p>

      <InlineConsentCard
        source="account_preferences"
        variant="full"
        className="mt-6"
        channels={["whatsapp", "email", "push", "advertising"]}
      />

      <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
        Every change is recorded with a timestamp so we can honour it across WhatsApp, email and
        browser notifications. Read more in our{" "}
        <Link to="/policies/privacy-policy" className="underline underline-offset-2">
          privacy policy
        </Link>
        .
      </p>
    </main>
  );
}
